import { useEffect, useState } from "react";
import { AllMembers } from "../../lib/discordTypes";
import {
  getAllMembers,
  getDiscordInitialMembers,
  getDiscordMembersPaginatedFrom,
  getDiscordMemberTags,
  getDiscordTotalGuildMembers,
} from "../../lib/discordQueries";

/**
 * Load all members for the given guild
 * @param guildId discord_guilds discord_id value
 * @returns
 *  data: all members data,
 *  isLoading: whether hook is still loading,
 *  allMembersLoaded: whether all members are loaded
 *  setData: state modifying function to update data
 *  filteredMemberRoles: filtered members by role
 *  setFilteredMembersRoles: state modifying function to update filtered members
 *  resetAll: function to reset filtered members
 *  loadAllMembers: callback to load all members from Supabase
 */
export default function useAllMembers(guildId: string) {
  const [loading, setLoading] = useState<boolean>(true);
  const [allMembers, setAllMembers] = useState<AllMembers[]>([]);
  const [filteredMembersRoles, setFilteredMembersRoles] = useState<
    AllMembers[] | null
  >([]);
  const [totalMembersCounter, setTotalMembersCounter] = useState<number | null>(
    null
  );
  const [allMembersLoaded, setAllMembersLoaded] = useState<boolean>(false);

  const loadMembersTags = async (guildId: string) => {
    const memberTags = await getDiscordMemberTags(guildId);

    if (!memberTags) {
      return [];
    }

    return memberTags;
  };

  const loadAllMembers = async (): Promise<void> => {
    setLoading(true);
    try {
      const members = (await getAllMembers(guildId)) as AllMembers[];
      const allMembersWithTags = await loadMembersTags(guildId);

      //Remove tags from members and add updated ones
      members.forEach((member) => {
        const memberWithTags = allMembersWithTags.find(
          (m) => m.id === member.id
        );
        member.tags = memberWithTags ? memberWithTags.tags : [];
      });

      setAllMembers(members as AllMembers[]);
      setTotalMembersCounter(members.length);
      setFilteredMembersRoles(members as AllMembers[]);

      setLoading(false);
    } finally {
      setAllMembersLoaded(true);
    }
  };

  const resetAll = () => {
    setFilteredMembersRoles(allMembers);
  };

  // update if guild id changes
  useEffect(() => {
    loadAllMembers();
  }, [guildId]);
  // if empty
  if (!guildId) {
    const data: AllMembers[] = [];
    return {
      data,
      isLoading: false,
      setData: setAllMembers,
      setFilteredMembersRoles,
      filteredMembersRoles,
      resetAll,
      loadAllMembers,
      allMembersLoaded,
      totalMembersCounter,
    };
  }

  return {
    data: allMembers,
    setData: setAllMembers,
    isLoading: loading,
    filteredMembersRoles,
    setFilteredMembersRoles,
    resetAll,
    loadAllMembers,
    loadMembersTags,
    totalMembersCounter,
    allMembersLoaded,
  };
}
